import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import { useLazyGetSurveyStatisticsQuery } from "@/redux/service.js";
import {
  PRME_ORGANIZATION_ID,
  SAMPLE_SURVEY_PATH_PARAM_VALUE,
} from "@/shared/utils/constants";
import SubmissionConfirmationModal from "@/domains/survey/components/SubmissionConfirmationModal";
import { SurveySummaryProps } from "@/features/survey/summary/types";
import SectionCard from "@/features/survey/summary/components/SectionCard";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner.jsx";
import SurveyHeader from "@/features/survey/summary/components/SurveyHeader";
import { useSurveyPermissions } from "@/features/survey/hooks/useSurveyPermissions";
import { selectCurrentUser } from "@/redux/authentication/selectors";

import "./styles.css";

const SurveySummary = ({ survey }: SurveySummaryProps) => {
  const { t } = useTranslation();
  const location = useLocation();

  const isSample = location.pathname.includes(SAMPLE_SURVEY_PATH_PARAM_VALUE);
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [getSurveyStatistics, surveyStatisticsResponse] =
    useLazyGetSurveyStatisticsQuery();
  const currentUser = useSelector(selectCurrentUser);
  const {
    canView,
    canEdit,
    isLoading: isLoadingPermissions,
  } = useSurveyPermissions(survey, currentUser);

  useEffect(() => {
    getSurveyStatistics({
      survey: survey.id,
      organization: isSample ? PRME_ORGANIZATION_ID : null,
    });
  }, [survey]);

  if (surveyStatisticsResponse.isLoading || isLoadingPermissions) {
    return (
      <div className="flex justify-center full-width">
        <BaseSpinner />
      </div>
    );
  }

  if (!canView) {
    return <Navigate to="/" />;
  } else if (!canEdit) {
    return <Navigate to={`/reports/${survey.id}/overview`} />;
  }

  return (
    surveyStatisticsResponse.isSuccess && (
      <div className="page-layout">
        <SurveyHeader
          survey={survey}
          statistics={surveyStatisticsResponse.data}
          setOpenConfirmationModal={setOpenConfirmationModal}
          isSample={isSample}
        />

        <div className="survey-sections-overview-content-container flex flex-col gap-400 items-start full-width">
          <div className="flex flex-col gap-200">
            <h3 className="header-m-medium">{t("survey.labels.principles")}</h3>

            <p className="body-m-regular">
              {t("survey.labels.clickToStartAnswering")}
            </p>
          </div>

          <div className="survey-sections-overview-content gap-400 full-width">
            {surveyStatisticsResponse.data
              .slice()
              .sort((a, b) => a.index - b.index)
              .map((section) => {
                return <SectionCard isSample={isSample} section={section} />;
              })}
          </div>
        </div>

        <SubmissionConfirmationModal
          setOpen={setOpenConfirmationModal}
          survey={survey}
          open={openConfirmationModal}
        />
      </div>
    )
  );
};

export default SurveySummary;
